import React, { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@cotera/client/v0/components/ui/command';
import { useDatasetWorkflowOptions } from '@cotera/client/app/v1/trigger/hooks';
import { cn } from '@cotera/client/v0/lib/utils';
import { TOOL_INPUT_CONTROL_HEIGHT } from '../tool-input-styles';
import type { ToolInputControlProps } from './index';

/** The saved value read back as a string[], tolerating a partly-typed param. */
const readWorkflowIds = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.filter((id): id is string => typeof id === 'string')
    : [];

/**
 * Picks the workflows a "Run Dataset Workflows" node targets, by name.
 *
 * The field is a list of workflow uuids, and the generic editor renders that as
 * a JSON box — asking the builder to paste ids they have no way to look up from
 * here. The options are the workflows actually bound to the dataset the sibling
 * `datasetId` names, which is the same set the tool will run against, so a name
 * chosen here cannot resolve to a workflow the run then ignores.
 *
 * An empty selection is the tool's own default: `workflowIds: null` runs every
 * workflow bound to the dataset. So leaving this blank is "all of them", and the
 * placeholder says as much rather than reading as an unconfigured field.
 *
 * A saved id that no longer names a bound workflow — unbound since, or from a
 * dataset the node was later pointed away from — still renders as itself rather
 * than vanishing, so the change reads as the user's to make, not a silent edit.
 */
export const WorkflowMultiSelectControl: React.FC<ToolInputControlProps> = ({
  field,
  params,
  setValues,
}) => {
  const [open, setOpen] = useState(false);
  const rawDatasetId = params['datasetId'];
  const datasetId =
    typeof rawDatasetId === 'string' && rawDatasetId !== ''
      ? rawDatasetId
      : null;
  const { options, isLoading } = useDatasetWorkflowOptions(datasetId);
  const selected = readWorkflowIds(params[field.name]);

  const nameFor = (id: string): string =>
    options.find((option) => option.id === id)?.name ?? id;

  const toggle = (id: string): void => {
    const next = selected.includes(id)
      ? selected.filter((existing) => existing !== id)
      : [...selected, id];
    // An empty list means "no workflows named", which the tool reads as "all of
    // them" — so write `undefined` rather than `[]`, keeping the param unset and
    // the node's saved config faithful to what the picker shows.
    setValues({ [field.name]: next.length === 0 ? undefined : next });
  };

  if (datasetId === null) {
    return (
      <Text.Caption className="text-muted-foreground block">
        Choose a dataset first, then pick which of its workflows to run.
      </Text.Caption>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              TOOL_INPUT_CONTROL_HEIGHT,
              'w-full justify-between rounded-md font-normal'
            )}
          >
            <span className="truncate">
              {selected.length === 0
                ? 'All workflows on this dataset'
                : `${selected.length} workflow${
                    selected.length === 1 ? '' : 's'
                  } selected`}
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-(--radix-popover-trigger-width) p-0"
        >
          <Command>
            <CommandInput placeholder="Search workflows…" />
            <CommandList>
              <CommandEmpty>
                {isLoading
                  ? 'Loading workflows…'
                  : 'No workflows are bound to this dataset.'}
              </CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected.includes(option.id);
                  return (
                    <CommandItem
                      key={option.id}
                      value={`${option.name} ${option.id}`}
                      onSelect={() => toggle(option.id)}
                    >
                      <Check
                        className={
                          isSelected
                            ? 'mr-2 size-4 opacity-100'
                            : 'mr-2 size-4 opacity-0'
                        }
                      />
                      <span className="truncate">{option.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((id) => (
            <Badge key={id} variant="secondary" className="gap-1 pr-1">
              <span className="truncate">{nameFor(id)}</span>
              <button
                type="button"
                onClick={() => toggle(id)}
                title="Remove workflow"
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
};
